import { ProductMetadata } from 'indices-list'

import Big from 'big.js'
import { ProductType } from 'sdk'
import { assertMultiply } from './numbers-helper'

export interface InfoLabelItem {
  name: string
  value: string
  isPercent?: boolean
  isCurrency?: boolean
}

export interface BalanceOfProps {
  [key: string]: { balance: Big; totalSupply: Big }
}

export interface CapitalizationOfProps {
  [key: string]: { cap: Big }
}

export interface AssetComposition {
  value: Big
  id: string
}

export interface ProductListData {
  id: string
  symbol: string
  name: string
  basePrice: string
}

/**
 * IT: Builds up asset composition from index history
 * NOTE: The query results are ordered by timestamp, so we can just take the first one
 * @param indexes
 */
export const buildAssetComposition = (
  balances: { id: string; balance: Big }[],
): AssetComposition[] => {
  const totalInvestmentValue = balances.reduce((a, b) => a.plus(b.balance), Big(0)).toString()
  return roundOffByLargestRemainder(
    balances.map<AssetComposition>((asset: { id: string; balance: Big }) => {
      assertMultiply(asset.balance.div(totalInvestmentValue), 100)
      const assetPercentage = asset.balance.gt(0)
        ? asset.balance.div(totalInvestmentValue).mul(100)
        : Big(0)
      return {
        id: asset.id,
        value: assetPercentage,
      }
    }),
  )
}

// largest remainder algo
// https://en.wikipedia.org/wiki/Largest_remainder_method
export const roundOffByLargestRemainder = (quotients: AssetComposition[]): AssetComposition[] => {
  if (quotients.length === 0) {
    return []
  }
  // rounder everything down (precision 2)
  const roundedDown = quotients.map((q) => ({ id: q.id, value: q.value.round(2, Big.roundDown) }))

  // sort the numbers largest to smallest
  const sorted = roundedDown.sort((a, b) => b.value.minus(a.value).toNumber())

  // add all the rounded down values
  const sumOfRoundedDown = roundedDown.reduce((a, b) => a.plus(b.value), Big(0))

  // get the difference between the sum and 100
  let difference = Big(100).minus(sumOfRoundedDown)

  // while difference is not 100 add 0.01, starting by largest number
  let i = 0

  while (difference.gt(0)) {
    if (!sorted[i]) {
      i = 0
    } else {
      sorted[i].value = sorted[i].value.plus('0.01')
      difference = difference.minus('0.01')
      i++
    }
  }

  return sorted
}

export const noStakingProduct = (item: ProductMetadata) => item.productType !== ProductType.STAKING
